/**
 * FileDocumentsCard — the typed card for `correct_file_documents`
 * (ChatMessage.TOOL_CARDS; backend allowlist agent_types.RENDERED_TOOLS).
 *
 * Arc corrected how a file is split into documents ("merge documents 3 and 4",
 * "a document starts on page 47", "call document 5 …"). Automatic first (plan
 * §7d): the correction is already applied — nothing to approve. The card shows
 * what changed (the documents that went, the documents there are now) and
 * offers Undo, which appends the inverse ruling through the same endpoint the
 * inventory panel uses. Undo is only offered while Arc's ruling is still the
 * file's latest edit; after later edits the file's own Edits list is the place.
 * A refused request renders no card — Arc's prose says why.
 */
import React, { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';

import { api } from '../api';
import { announceSegmentsChanged } from '../hooks/useDocumentSegments';

const S = {
  card: { marginTop: 8, border: '1px solid #d9cdf5', borderRadius: 8, background: '#faf8ff', overflow: 'hidden', fontSize: 13 },
  head: { padding: '8px 12px', background: '#efe8fd', fontWeight: 600, display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' },
  hint: { fontSize: 12, color: '#8a7fae', fontWeight: 400 },
  block: { padding: '6px 12px 8px', borderTop: '1px solid #eee6fb' },
  label: { fontSize: 11, color: '#8a7fae', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 2 },
  row: { display: 'flex', gap: 8, alignItems: 'baseline', padding: '2px 0' },
  num: { fontWeight: 700, color: '#8a7fae', fontSize: 12, minWidth: 18, textAlign: 'right' },
  gone: { textDecoration: 'line-through', color: '#889' },
  pages: { fontSize: 12, color: '#667', whiteSpace: 'nowrap' },
  actions: { display: 'flex', gap: 10, alignItems: 'center', padding: '8px 12px', borderTop: '1px solid #eee6fb', background: '#fdfcff', flexWrap: 'wrap' },
  btn: { padding: '4px 11px', borderRadius: 6, border: '1px solid #5b3fa8', background: '#fff', color: '#5b3fa8', cursor: 'pointer', fontSize: 13 },
  link: { fontSize: 12, color: '#5b3fa8', textDecoration: 'underline', cursor: 'pointer', background: 'none', border: 'none', padding: 0 },
  done: { fontSize: 12, color: '#2c6e49', fontWeight: 600 },
  note: { fontSize: 12, color: '#7a5b00' },
  err: { color: '#c0392b', fontSize: 12 },
};

const keyOf = (d) => `${d.page_start}-${d.page_end}|${d.title || ''}`;
const pagesOf = (d) => (d.page_start === d.page_end ? `p. ${d.page_start}` : `pp. ${d.page_start}–${d.page_end}`);
const sameRuling = (edit, ruling) => !!edit && !!ruling && edit.action === ruling.action && Number(edit.page) === Number(ruling.page)
  && (edit.to_page ?? null) === (ruling.to_page ?? null) && (edit.value ?? null) === (ruling.value ?? null);

function Rows({ docs, gone = false }) {
  const shown = docs.slice(0, 6);
  return (
    <>
      {shown.map((d) => (
        <div key={keyOf(d)} style={S.row}>
          <span style={S.num}>{d.number}</span>
          <span style={gone ? S.gone : undefined}>{d.title || 'Untitled document'}</span>
          <span style={S.pages}>{pagesOf(d)}</span>
        </div>
      ))}
      {docs.length > shown.length && <div style={S.hint}>… {docs.length - shown.length} more</div>}
    </>
  );
}

export default function FileDocumentsCard({ result }) {
  const { caseId } = useParams();
  const [state, setState] = useState('applied');      // applied | busy | undone | stale
  const [error, setError] = useState('');
  const before = result?.before || [];
  const after = result?.after || [];
  const { removed, added } = useMemo(() => {
    const was = new Set(before.map(keyOf));
    const now = new Set(after.map(keyOf));
    return { removed: before.filter((d) => !now.has(keyOf(d))), added: after.filter((d) => !was.has(keyOf(d))) };
  }, [before, after]);

  if (result?.status !== 'applied') return null;
  const fileId = result.undo?.document_id || result.file?.id;

  const undo = async () => {
    if (!fileId || state !== 'applied') return;
    setState('busy'); setError('');
    try {
      // only reverse Arc's own ruling: if the file was edited since, its Edits list is the place
      const current = await api.get(`/documents/${fileId}/segments`);
      if (!sameRuling((current.edits || [])[0], result.ruling)) { setState('stale'); return; }
      await api.post(`/documents/${fileId}/segments/decisions/undo`, {});
      announceSegmentsChanged(fileId, 'arc');
      setState('undone');
    } catch (e) {
      setError(e?.message || 'Could not undo');
      setState('applied');
    }
  };

  return (
    <div style={S.card} data-testid="file-documents-card" data-state={state}>
      <div style={S.head}>
        <span>✎ {result.summary || 'Corrected the documents in this file'}</span>
        <span style={S.hint}>{result.file?.filename}</span>
      </div>
      {removed.length > 0 && (
        <div style={S.block}>
          <div style={S.label}>Before</div>
          <Rows docs={removed} gone />
        </div>
      )}
      {added.length > 0 && (
        <div style={S.block}>
          <div style={S.label}>Now</div>
          <Rows docs={added} />
        </div>
      )}
      <div style={S.actions}>
        <span style={S.hint}>{after.length} {after.length === 1 ? 'document' : 'documents'} in this file{result.recut === 'queued' ? ' · its documents update in a moment' : ''}</span>
        {state === 'undone'
          ? <span style={S.done}>Undone</span>
          : state === 'stale'
            ? <span style={S.note}>This file was edited again since — undo from the file’s Edits list.</span>
            : <button type="button" style={S.btn} onClick={undo} disabled={state === 'busy'} title="Reverse this correction">{state === 'busy' ? 'Undoing…' : 'Undo'}</button>}
        {caseId && fileId && (
          <a style={S.link} href={`/case/${caseId}/inventory?unit=files&doc=${fileId}`}>Open the file</a>
        )}
        {error && <span style={S.err}>{error}</span>}
      </div>
    </div>
  );
}
